/**
 * THE PAPER PREVIEW — a PrintDocument as the piece of paper an ESC/POS
 * text-mode printer produces from it.
 *
 * Two halves, both PORTS of the gateway:
 *
 *   1. the EMITTER — `emitDocumentV2ESCPOS` and its alert / drawerPulse / logo
 *      arms (apps/gateway/internal/renderer/document_v2.go, escpos_document.go,
 *      alert.go, escpos.go). It walks the blocks exactly as the gateway does:
 *      the `only: ['escpos']` branch, the same column counts, the same
 *      size:role / size:rush / font:kitchen resolution from the PRINTER's size
 *      settings — and instead of appending bytes it drives…
 *   2. …the PRINTER — the state machine of
 *      apps/gateway/internal/renderer/preview_text.go (`PreviewGrid`): font,
 *      emphasis, size, underline, inversion, colour and alignment as the Epson
 *      command reference defines them, including the rule that `ESC !` sets
 *      emphasis, underline and font TOGETHER with size. The emitter therefore
 *      re-states the selected style in every size change, exactly as the
 *      gateway now does (escpos_emphasis.go, owner decision 2026-09-27), so
 *      `bold+` followed by `size:1x2` prints bold.
 *
 * The production stores print through the gateway's ESC/POS text path, so this
 * is the paper a manager actually gets. It is held to the gateway by the
 * preview-grid parity corpus: for every document in the vendored parity corpus
 * at 80 and 76 mm and every size setting, `renderPaperGrid(doc, …)` must equal
 * the grid the Go state machine produces from the bytes the Go emitter writes
 * (packages/print-conformance/fixtures/preview-grid/, generated by
 * apps/gateway `TestPreviewGridCorpus`). A divergence is a red test.
 *
 * What it deliberately does NOT model: the glyph shapes (a font draws them), the
 * printer's line spacing (a setting nothing changes), and a raster logo's
 * dimensions (the gateway decodes the image; here it is a placeholder row).
 */
import type { PrintBlock, PrintDocument } from '@order-up/models';
import { measureLines } from '@order-up/models';

import { type GrayImage, renderLogoRaster } from './logoRaster';
import {
  charColumns,
  columnsForPaperWidth,
  displayWidthFor,
  fillLine,
  lineColumns,
  padLinePrefixedFor,
  type PaperCharacterSet,
  printedVerbatimFor,
  sanitizeFor,
  wrapHangingFor,
  writeInLine,
} from './textLayout';

export type { PaperCharacterSet } from './textLayout';

// ─── The grid (preview_text.go PreviewGridPage, json tags) ──────────────────

export type PaperFontSize = 'Small' | 'Medium' | 'Large';
export type PaperWidth = 58 | 76 | 80;

/** A maximal stretch of one line printed in one printer state. */
export interface PaperRun {
  /** The characters, as the code page prints them. */
  readonly s: string;
  /** Width multiplier (1..8). */
  readonly w: number;
  /** Height multiplier (1..8). */
  readonly h: number;
  readonly fontB?: boolean;
  readonly bold?: boolean;
  readonly ul?: boolean;
  readonly inv?: boolean;
  /** The ribbon's second colour — only a two-colour (76 mm impact) head prints it. */
  readonly red?: boolean;
}

/** One thing the paper does, in order. */
export type PaperRow =
  /** A printed line. `x` is the dot offset of its first character — the alignment, applied. */
  | { readonly t: 'text'; readonly x?: number; readonly runs: readonly PaperRun[] }
  /** A line feed with nothing in the buffer. */
  | { readonly t: 'blank' }
  /** ESC J n. */
  | { readonly t: 'feed'; readonly dots: number }
  | { readonly t: 'cut' }
  | { readonly t: 'qr'; readonly data?: string; readonly align: 'left' | 'center' | 'right' }
  /**
   * A GS v 0 raster, dot for dot: `w` dots wide x `h` rows, `bits` the packed
   * rows as hex (MSB = leftmost dot, set = black). Absent `bits` means the
   * preview could not decode the image (it prints; its dots are not shown).
   */
  | {
      readonly t: 'image';
      readonly align: 'left' | 'center' | 'right';
      readonly w?: number;
      readonly h?: number;
      readonly bits?: string;
    }
  /** ESC B n t: n beeps of t x 100 ms. */
  | { readonly t: 'buzzer'; readonly beeps?: number; readonly units?: number }
  /** ESC p (buffered) or DLE DC4 (real-time) connector pulse — a drawer or a dk-port buzzer. */
  | { readonly t: 'pulse'; readonly pin?: number; readonly units?: number; readonly realtime?: boolean };

export interface PaperGrid {
  readonly paperWidthMm: number;
  /** Font A columns at 1x1. */
  readonly columns: number;
  /** Print-area width in dots (203 dpi). */
  readonly dots: number;
  /**
   * Present only for a Chinese printer: its runs hold double-byte characters
   * two columns wide (design 2026-10-24 §1). Absent means Western — every
   * existing grid, unchanged.
   */
  readonly characterSet?: 'Chinese';
  readonly rows: readonly PaperRow[];
}

/** The printer a document is previewed on — the same three settings a printer document carries. */
export interface PaperProfile {
  readonly paperWidthMm: PaperWidth;
  readonly kitchenFontSize?: PaperFontSize;
  readonly receiptFontSize?: PaperFontSize;
  /** The printer's character set — default Western (PC850, one rune one column). */
  readonly characterSet?: PaperCharacterSet;
}

/**
 * How a logo block's `imageBase64` becomes pixels. The gateway decodes with
 * image/png; the browser decodes asynchronously (`decodeGrayPngBase64`), so the
 * caller decodes first and hands the result in. Returns null for an image the
 * gateway would fail to decode (it then prints the text alternative); returns
 * `undefined` when the caller simply does not know (a placeholder is drawn).
 */
export interface PaperRenderOptions {
  readonly decodeLogo?: (imageBase64: string) => GrayImage | null | undefined;
  /**
   * Which tier's paper: 'escpos' (default) — the gateway, which prints both
   * production stores' tickets; 'pos' — a till printing the document itself
   * (raw socket or Epson SDK), which reads the `only: ['pos']` branch and has
   * no mid-document reset. Both are pinned against the gateway's printer
   * model (preview-grid/*.json and pos-escpos-grids.json).
   */
  readonly protocol?: 'escpos' | 'pos';
}

/** Font A / Font B cell width in dots at 203 dpi (TM-m30III). */
export const CELL_WIDTH_DOTS = 12;
export const FONT_B_WIDTH_DOTS = 9;
export const CELL_HEIGHT_DOTS = 24;
export const FONT_B_HEIGHT_DOTS = 17;

/**
 * The 1x1 column count when a printer's "Small" size setting selects Font B —
 * owner decision 2026-09-28: a Small printer is LAID OUT for Font B's real
 * line, not padded to the Font A count with the right of the paper empty.
 *
 * Thermal: the print area (Font A columns x CELL_WIDTH_DOTS) over
 * FONT_B_WIDTH_DOTS — 80 mm 576 / 9 = 64, 58 mm 384 / 9 = 42 (a partial cell
 * does not print). 76 mm (TM-U220B impact) keeps its Font A 40: nothing in the
 * fleet's code knows that head's Font B pitch. The port of the gateway's
 * `fontBColumnsForPaperWidth` (escpos.go); `paperWidthSmallFontColumns` in
 * @order-up/models is the same table, pinned with Go and Dart by the
 * `smallFontColumnsForPaperWidth` conformance op.
 */
export function smallFontColumnsForPaperWidth(mm: number): number {
  const fontA = columnsForPaperWidth(mm);
  if (mm === 76) return fontA;
  return Math.trunc((fontA * CELL_WIDTH_DOTS) / FONT_B_WIDTH_DOTS);
}

// ─── The printer (preview_text.go gridBuilder) ──────────────────────────────

interface PrinterState {
  w: number;
  h: number;
  fontB: boolean;
  bold: boolean;
  underline: boolean;
  invert: boolean;
  red: boolean;
  align: 0 | 1 | 2;
}

const freshState = (): PrinterState => ({
  w: 1, h: 1, fontB: false, bold: false, underline: false, invert: false, red: false, align: 0,
});

const ALIGN_NAMES = ['left', 'center', 'right'] as const;

function runOf(st: PrinterState, s: string): PaperRun {
  return {
    s,
    w: st.w,
    h: st.h,
    ...(st.fontB ? { fontB: true } : {}),
    ...(st.bold ? { bold: true } : {}),
    ...(st.underline ? { ul: true } : {}),
    ...(st.invert ? { inv: true } : {}),
    ...(st.red ? { red: true } : {}),
  };
}

function sameRun(a: PrinterState, b: PrinterState): boolean {
  return a.w === b.w && a.h === b.h && a.fontB === b.fontB && a.bold === b.bold &&
    a.underline === b.underline && a.invert === b.invert && a.red === b.red;
}

/**
 * The print head. Every method is one ESC/POS command the gateway emitter
 * writes, with the effect preview_text.go gives that command's bytes.
 */
class PaperPrinter {
  private st: PrinterState = freshState();
  private line: { ch: string; st: PrinterState }[] = [];
  private lineAlign: 0 | 1 | 2 = 0;
  private used = 0;
  readonly rows: PaperRow[] = [];

  constructor(
    readonly dots: number,
    private readonly charset: PaperCharacterSet = 'Western',
  ) {}

  private textRow(): PaperRow {
    const runs: PaperRun[] = [];
    let curSt = this.line[0].st;
    let text = '';
    for (const c of this.line) {
      if (!sameRun(c.st, curSt)) {
        runs.push(runOf(curSt, text));
        text = '';
        curSt = c.st;
      }
      text += c.ch;
    }
    runs.push(runOf(curSt, text));
    let x = 0;
    if (this.used < this.dots) {
      if (this.lineAlign === 1) x = Math.trunc((this.dots - this.used) / 2);
      else if (this.lineAlign === 2) x = this.dots - this.used;
    }
    return x > 0 ? { t: 'text', x, runs } : { t: 'text', runs };
  }

  /** LF. */
  lf(): void {
    if (this.line.length === 0) {
      this.rows.push({ t: 'blank' });
      return;
    }
    this.rows.push(this.textRow());
    this.line = [];
    this.used = 0;
  }

  private flushIfContent(): void {
    if (this.line.length > 0) this.lf();
  }

  char(ch: string): void {
    // A double-byte character is two cells wide, and never split across lines.
    const w = (this.st.fontB ? FONT_B_WIDTH_DOTS : CELL_WIDTH_DOTS) * this.st.w * charColumns(ch, this.charset);
    if (this.line.length > 0 && this.used + w > this.dots) this.lf();
    if (this.line.length === 0) this.lineAlign = this.st.align;
    this.line.push({ ch, st: { ...this.st } });
    this.used += w;
  }

  /** A string of already-encodable characters, then nothing (the caller feeds). */
  chars(s: string): void {
    for (const ch of s) {
      const cp = ch.codePointAt(0) ?? 0;
      if (cp < 0x20 || cp === 0x7f) continue;
      this.char(ch);
    }
  }

  /** ESC @ (+ ESC t 2): every mode reset, and the print buffer cleared. */
  init(): void {
    this.st = freshState();
    this.line = [];
    this.used = 0;
  }

  /** ESC ! n — font, emphasis, size and underline, together. */
  escBang(n: number): void {
    this.st.fontB = (n & 0x01) !== 0;
    this.st.bold = (n & 0x08) !== 0;
    this.st.h = (n & 0x10) !== 0 ? 2 : 1;
    this.st.w = (n & 0x20) !== 0 ? 2 : 1;
    this.st.underline = (n & 0x80) !== 0;
  }

  /** GS ! n. */
  gsBang(n: number): void {
    this.st.w = ((n >> 4) & 0x07) + 1;
    this.st.h = (n & 0x07) + 1;
  }

  bold(on: boolean): void {
    this.st.bold = on;
  }
  underline(on: boolean): void {
    this.st.underline = on;
  }
  invert(on: boolean): void {
    this.st.invert = on;
  }
  red(on: boolean): void {
    this.st.red = on;
  }
  fontB(on: boolean): void {
    this.st.fontB = on;
  }

  /** ESC a — honoured only at the beginning of a line. */
  align(a: 0 | 1 | 2): void {
    if (this.line.length === 0) this.st.align = a;
  }

  /** ESC J n. */
  feedDots(n: number): void {
    this.flushIfContent();
    this.rows.push({ t: 'feed', dots: n });
  }

  /** GS V. */
  cut(): void {
    this.flushIfContent();
    this.rows.push({ t: 'cut' });
  }

  /** GS ( k store + print. */
  qr(data: string): void {
    this.flushIfContent();
    this.rows.push(data ? { t: 'qr', data, align: ALIGN_NAMES[this.st.align] } : { t: 'qr', align: ALIGN_NAMES[this.st.align] });
  }

  /** GS v 0 — the raster's dots, or a placeholder when they are unknown. */
  image(raster?: { width: number; height: number; bits: string }): void {
    this.flushIfContent();
    this.rows.push(
      raster
        ? { t: 'image', align: ALIGN_NAMES[this.st.align], w: raster.width, h: raster.height, bits: raster.bits }
        : { t: 'image', align: ALIGN_NAMES[this.st.align] },
    );
  }

  /** ESC B n t. */
  buzzer(beeps: number, units: number): void {
    this.rows.push({ t: 'buzzer', ...(beeps ? { beeps } : {}), ...(units ? { units } : {}) });
  }

  /** ESC p m t1 t2 / DLE DC4 1 m t. */
  pulse(pin: number, units: number, realtime: boolean): void {
    this.rows.push({
      t: 'pulse',
      ...(pin ? { pin } : {}),
      ...(units ? { units } : {}),
      ...(realtime ? { realtime: true } : {}),
    });
  }

  finish(): void {
    this.flushIfContent();
  }
}

// ─── Scale (document_scale.go) ──────────────────────────────────────────────

/** ESC ! operands the emitter uses: 1x1, 1x2 (double height), 2x2. */
const SIZE_NORMAL = 0x00;
const SIZE_DOUBLE_H = 0x10;
const SIZE_DOUBLE = 0x30;

interface Slot {
  readonly size: number;
  readonly cols: number;
}

interface KitchenScale {
  readonly header: number;
  readonly item: number;
  readonly modifier: number;
  readonly itemCols: number;
  readonly modCols: number;
  readonly headCols: number;
}

function kitchenScaleForSize(fontSize: PaperFontSize, w: number): KitchenScale {
  const half = Math.trunc(w / 2);
  if (fontSize === 'Small') {
    return { header: SIZE_DOUBLE_H, item: SIZE_NORMAL, modifier: SIZE_NORMAL, itemCols: w, modCols: w, headCols: w };
  }
  if (fontSize === 'Large') {
    return { header: SIZE_DOUBLE, item: SIZE_DOUBLE, modifier: SIZE_DOUBLE, itemCols: half, modCols: half, headCols: half };
  }
  return { header: SIZE_DOUBLE, item: SIZE_DOUBLE, modifier: SIZE_DOUBLE_H, itemCols: half, modCols: w, headCols: half };
}

// ─── Alerts and pulses (alert.go, escpos.go) ────────────────────────────────

const BUZZER_PROFILES: Readonly<Record<string, { beeps: number; num: number; den: number }>> = {
  patternA: { beeps: 1, num: 1, den: 1 },
  patternB: { beeps: 2, num: 1, den: 1 },
  patternC: { beeps: 3, num: 1, den: 2 },
  patternD: { beeps: 1, num: 2, den: 1 },
  patternE: { beeps: 4, num: 3, den: 2 },
};

const clampByte = (v: number): number => (v < 1 ? 1 : v > 255 ? 255 : v);
const pulseUnits2ms = (ms: number): number => clampByte(Math.trunc((ms + 1) / 2));

function drawerPulseUnits(pulseMs: number): number {
  if (pulseMs <= 0) return 1;
  const units = Math.trunc((pulseMs + 50) / 100);
  return units < 1 ? 1 : units > 5 ? 5 : units;
}

// ─── The emitter (document_v2.go + escpos_document.go) ──────────────────────

/** A block the ESC/POS path cannot preview here — the preview says so rather than guess. */
export class PaperPreviewUnsupportedError extends Error {
  constructor(readonly kind: string) {
    super(`the paper preview cannot show a "${kind}" block`);
    this.name = 'PaperPreviewUnsupportedError';
  }
}

function printsOn(b: PrintBlock, protocol: 'escpos' | 'pos'): boolean {
  const only = (b as { only?: readonly string[] }).only;
  return !only || only.length === 0 || only.includes(protocol);
}

function parseWxH(arg: string): [number, number] {
  const parts = arg.split('x');
  if (parts.length < 2) return [1, 1];
  const clamp = (s: string) => {
    const n = /^[+-]?\d+$/.test(s) ? Number.parseInt(s, 10) : 0;
    return n < 1 ? 1 : n > 8 ? 8 : n;
  };
  return [clamp(parts[0]), clamp(parts.slice(1).join('x'))];
}

class EscposPaperEmitter {
  /** Font A columns at 1x1. */
  private readonly width: number;
  /** Font B columns at 1x1 — what a `font:*` selecting the Small font lays out at. */
  private readonly smallWidth: number;
  private readonly twoColor: boolean;
  private readonly kitchenFont: PaperFontSize;
  private readonly receiptFont: PaperFontSize;
  private cols: number;

  private readonly paperWidthMm: number;
  private readonly protocol: 'escpos' | 'pos';
  private readonly charset: PaperCharacterSet;
  /** Style the stream has selected — re-stated by every size change (see `size`). */
  private bold = false;
  private underline = false;
  private fontB = false;
  /** The last size:/scale: command — a `font:*` after it re-resolves its columns. */
  private sizeCmd = '';
  private started = false;

  constructor(
    private readonly p: PaperPrinter,
    profile: PaperProfile,
    private readonly options: PaperRenderOptions = {},
  ) {
    this.paperWidthMm = profile.paperWidthMm;
    this.protocol = options.protocol ?? 'escpos';
    this.charset = profile.characterSet ?? 'Western';
    this.width = columnsForPaperWidth(profile.paperWidthMm);
    this.smallWidth = smallFontColumnsForPaperWidth(profile.paperWidthMm);
    this.twoColor = profile.paperWidthMm === 76;
    this.kitchenFont = profile.kitchenFontSize ?? 'Medium';
    this.receiptFont = profile.receiptFontSize ?? 'Medium';
    this.cols = this.width;
  }

  /**
   * A size change that KEEPS the selected style (owner decision 2026-09-27).
   * The gateway re-states emphasis / underline / Font B in every ESC ! it
   * sends (apps/gateway escpos_emphasis.go `keepEmphasisAcrossSize`); the
   * till sizes with GS !, which never touched them. Either way the paper keeps
   * the style, and the printer model (which honours ESC !'s all-at-once
   * semantics) sees the same thing.
   */
  private size(n: number): void {
    this.p.escBang(n | (this.bold ? 0x08 : 0) | (this.underline ? 0x80 : 0) | (this.fontB ? 0x01 : 0));
  }

  private setBold(on: boolean): void {
    this.bold = on;
    this.p.bold(on);
  }

  private setUnderline(on: boolean): void {
    this.underline = on;
    this.p.underline(on);
  }

  private scale(role: string, w: number = this.width): Slot {
    const ks = kitchenScaleForSize(this.kitchenFont, w);
    switch (role) {
      case 'ticketHeader':
        return { size: ks.header, cols: ks.headCols };
      case 'ticketItem':
        return { size: ks.item, cols: ks.itemCols };
      case 'ticketModifier':
        return { size: ks.modifier, cols: ks.modCols };
      case 'display':
        return { size: SIZE_DOUBLE, cols: Math.trunc(w / 2) };
      case 'heading':
      case 'total':
        return { size: SIZE_DOUBLE_H, cols: w };
      default:
        return { size: SIZE_NORMAL, cols: w };
    }
  }

  private rushScale(w: number = this.width): Slot {
    const ks = kitchenScaleForSize(this.kitchenFont, w);
    return { size: SIZE_DOUBLE, cols: Math.min(ks.itemCols, ks.headCols) };
  }

  /**
   * document_v2.go `v2Cols`: the columns of the current size (`sizeCmd`) in the
   * current font — Font B at the Small width (owner decision 2026-09-28).
   */
  private currentCols(): number {
    const base = this.fontB ? this.smallWidth : this.width;
    const c = this.sizeCmd;
    const colon = c.indexOf(':');
    const name = colon < 0 ? c : c.slice(0, colon);
    const arg = colon < 0 ? '' : c.slice(colon + 1);
    if (name === 'size') {
      if (arg === 'rush') return this.rushScale(base).cols;
      if (arg.startsWith('role:')) return this.scale(arg.slice('role:'.length), base).cols;
      return Math.trunc(base / parseWxH(arg)[0]);
    }
    if (name === 'scale') return Math.trunc(base / parseWxH(arg)[0]);
    return base;
  }

  /** appendLine: sanitize, one printed line per "\n". */
  private line(text: string): void {
    for (const ln of sanitizeFor(text, this.charset).split('\n')) {
      this.p.chars(ln);
      this.p.lf();
    }
  }

  /** appendAlertLine: the alert colour around the line, on two-colour hardware only. */
  private alertLine(text: string): void {
    if (!this.twoColor) {
      this.line(text);
      return;
    }
    this.p.red(true);
    this.line(text);
    this.p.red(false);
  }

  private command(c: string): void {
    const colon = c.indexOf(':');
    const name = colon < 0 ? c : c.slice(0, colon);
    const arg = colon < 0 ? '' : c.slice(colon + 1);
    switch (name) {
      case 'init':
        this.cols = this.width;
        this.sizeCmd = '';
        if (this.protocol === 'pos') {
          // The till has no reset op: the raw socket opens the job with ESC @,
          // and a later `init` only returns the font (PrintDocumentEmitter).
          if (this.fontB) this.p.fontB(false);
          this.fontB = false;
          if (!this.started) this.p.init();
          return;
        }
        this.bold = false;
        this.underline = false;
        this.fontB = false;
        this.p.init();
        return;
      case 'cut':
        this.p.cut();
        return;
      case 'bold+':
        this.setBold(true);
        return;
      case 'bold-':
        this.setBold(false);
        return;
      case 'underline+':
        this.setUnderline(true);
        return;
      case 'underline-':
        this.setUnderline(false);
        return;
      case 'invert+':
        this.p.invert(true);
        return;
      case 'invert-':
        this.p.invert(false);
        return;
      case 'alert+':
        if (this.twoColor) this.p.red(true);
        return;
      case 'alert-':
        if (this.twoColor) this.p.red(false);
        return;
      case 'align':
        this.p.align(arg === 'center' ? 1 : arg === 'right' ? 2 : 0);
        return;
      case 'size': {
        this.sizeCmd = c;
        this.cols = this.currentCols();
        if (arg === 'rush') {
          this.size(this.rushScale().size);
          return;
        }
        if (arg.startsWith('role:')) {
          this.size(this.scale(arg.slice('role:'.length)).size);
          return;
        }
        const [w, h] = parseWxH(arg);
        this.size((w >= 2 ? 0x20 : 0) | (h >= 2 ? 0x10 : 0));
        return;
      }
      case 'scale': {
        const [w, h] = parseWxH(arg);
        this.sizeCmd = c;
        this.cols = this.currentCols();
        this.p.gsBang(((w - 1) << 4) | (h - 1));
        return;
      }
      case 'font': {
        const small =
          arg === 'B' ||
          (arg === 'receipt' && this.receiptFont === 'Small') ||
          (arg === 'kitchen' && this.kitchenFont === 'Small');
        this.fontB = small;
        this.cols = this.currentCols();
        this.p.fontB(small);
        return;
      }
      case 'feedDots': {
        const n = Number.parseInt(arg, 10);
        if (n > 0) this.p.feedDots(n > 255 ? 255 : n);
        return;
      }
      default:
        // An unknown command appends nothing — as document_v2.go does.
        return;
    }
  }

  /** qrCodeCommand: nothing for empty or over-long data (2331 bytes, Model 2 / level M). */
  private qr(data: string): void {
    const bytes = new TextEncoder().encode(data).length;
    if (bytes === 0 || bytes > 2331) return;
    this.p.qr(data);
  }

  private wrapBlock(text: string, indent: string, alertColor: boolean): void {
    const cs = this.charset;
    const lines = wrapHangingFor(sanitizeFor(text, cs), this.cols, this.cols - displayWidthFor(indent, cs), cs);
    lines.forEach((ln, i) => {
      const out = i === 0 ? ln : indent + ln;
      if (alertColor) this.alertLine(out);
      else this.line(out);
    });
  }

  block(b: PrintBlock): void {
    this.blockInner(b);
    this.started = true;
  }

  private blockInner(b: PrintBlock): void {
    switch (b.kind) {
      case 'cmd':
        for (const c of b.commands) this.command(c);
        return;
      case 'text':
        if (b.verbatim) {
          for (const ln of b.text.split('\n')) {
            this.p.chars(printedVerbatimFor(ln, this.charset));
            this.p.lf();
          }
          return;
        }
        this.line(b.text);
        return;
      case 'cols':
        this.line(padLinePrefixedFor(b.prefix ?? '', b.left, b.right, this.cols, this.charset));
        return;
      case 'fill':
        this.line(fillLine(b.prefix ?? '', b.char, this.cols, this.charset));
        return;
      case 'writeIn':
        this.line(writeInLine(b.text, this.cols, this.charset));
        return;
      case 'feed':
        for (let i = 0; i < b.lines; i += 1) this.p.lf();
        return;
      case 'qrCode':
        this.qr(b.data);
        return;
      case 'wrap':
        this.wrapBlock(b.text, b.indent ?? '', b.alertColor === true);
        return;
      case 'measure':
        // The printer's own column count, supplied here as the emitters do.
        for (const ln of measureLines(b, this.cols)) this.line(ln);
        return;

      // The three non-text kinds (escpos_document.go emitBlockESCPOS).
      case 'alert': {
        const a = b.alert;
        const prof = BUZZER_PROFILES[a.pattern] ?? BUZZER_PROFILES.patternA;
        if (a.mode === 'printerBuzzer') {
          this.p.buzzer(
            clampByte(prof.beeps * a.repeat),
            clampByte(Math.trunc(Math.trunc((a.cycleMs * prof.num) / prof.den) / 100)),
          );
        } else if (a.mode === 'dkPort') {
          const bursts = Math.max(prof.beeps * a.repeat, 1);
          const pin = a.pin === 1 ? 1 : 0;
          const units = pulseUnits2ms(a.pulseMs);
          for (let i = 0; i < bursts; i += 1) this.p.pulse(pin, units, false);
        }
        return;
      }
      case 'drawerPulse': {
        const pin = b.pin === 1 ? 1 : 0;
        if (b.realtime) {
          this.p.pulse(pin, drawerPulseUnits(b.pulseMs), true);
          return;
        }
        if (b.pin === 0 && b.pulseMs <= 0) {
          this.p.pulse(0, 0x19, false);
          return;
        }
        this.p.pulse(pin, b.pulseMs > 0 ? clampByte(Math.trunc(b.pulseMs / 2)) : 0x19, false);
        return;
      }
      case 'logo': {
        // escpos_document.go BlockLogo → appendLogo: the raster when the image
        // decodes AND the printer has a raster path, otherwise the text
        // alternative — never both.
        if (b.imageBase64) {
          const decoded = this.options.decodeLogo?.(b.imageBase64);
          if (decoded === undefined) {
            // Undecoded here: the gateway prints SOMETHING; show where.
            if (this.paperWidthMm !== 76) {
              this.p.align(1);
              this.p.image();
              this.p.align(0);
              this.p.lf();
              return;
            }
          } else if (decoded !== null) {
            const raster = renderLogoRaster(decoded, this.paperWidthMm);
            if (raster) {
              this.p.align(1);
              this.p.image(raster);
              this.p.align(0);
              this.p.lf();
              return;
            }
          }
        }
        if (b.fallbackText) {
          this.p.align(1);
          this.line(b.fallbackText);
          this.line('');
          this.p.align(0);
        }
        return;
      }
      default: {
        // Exhaustive over PRINT_BLOCK_KINDS: a new kind is a compile error here.
        // At runtime a document from outside the type system (an unknown kind)
        // is refused by name rather than drawn as nothing.
        const unknown: never = b;
        throw new PaperPreviewUnsupportedError(String((unknown as { kind?: unknown }).kind));
      }
    }
  }
}

/**
 * The paper `doc` prints on an ESC/POS text-mode printer with this profile.
 *
 * A v2 (legacy-exact) document is what every production tier receives; a v1
 * document is refused, because the gateway gives it an implicit preamble and
 * item gaps this preview does not reproduce — showing a layout that is not the
 * one that prints would defeat the point.
 */
export function renderPaperGrid(
  doc: PrintDocument,
  profile: PaperProfile,
  options: PaperRenderOptions = {},
): PaperGrid {
  if (doc.version < 2) throw new PaperPreviewUnsupportedError(`version ${doc.version} document`);
  const columns = columnsForPaperWidth(profile.paperWidthMm);
  const charset = profile.characterSet ?? 'Western';
  const printer = new PaperPrinter(columns * CELL_WIDTH_DOTS, charset);
  const emitter = new EscposPaperEmitter(printer, profile, options);
  for (const block of doc.blocks) {
    if (printsOn(block, options.protocol ?? 'escpos')) emitter.block(block);
  }
  printer.finish();
  return {
    paperWidthMm: profile.paperWidthMm,
    columns,
    dots: printer.dots,
    ...(charset === 'Chinese' ? { characterSet: 'Chinese' as const } : {}),
    rows: printer.rows,
  };
}

/**
 * Does a printer's Typographic receipts setting (`renderMode: 'raster'`) change
 * how THIS document prints? Design 2026-10-24 §2: only a receipt-family
 * document (customer / merchant receipt, guest check), only on paper with a
 * graphics path (58 / 80 mm — the 76 mm impact head has none), only on a
 * Western printer — a Chinese printer's receipts stay text on both tiers (its
 * resident font already prints hanzi). A kitchen-family chit is always text.
 * Everything else prints the text path.
 */
export function typographicApplies(input: {
  readonly renderMode?: 'text' | 'raster';
  readonly paperWidthMm: PaperWidth;
  readonly receiptFamily: boolean;
  readonly characterSet?: PaperCharacterSet;
}): boolean {
  return (
    input.renderMode === 'raster' &&
    input.receiptFamily &&
    input.paperWidthMm !== 76 &&
    (input.characterSet ?? 'Western') !== 'Chinese'
  );
}

/** The printed text of one row (runs joined) — for tests and accessible text. */
export function paperRowText(row: PaperRow): string {
  return row.t === 'text' ? row.runs.map((r) => r.s).join('') : '';
}

/** The columns a run occupies — a Chinese printer's double-byte characters count two. */
export function paperRunColumns(run: PaperRun, charset: PaperCharacterSet = 'Western'): number {
  return lineColumns(run.s, charset);
}

/** Width of a run in dots: its columns x its cell x its width multiplier. */
export function paperRunDots(run: PaperRun, charset: PaperCharacterSet = 'Western'): number {
  return paperRunColumns(run, charset) * (run.fontB ? FONT_B_WIDTH_DOTS : CELL_WIDTH_DOTS) * run.w;
}
